import { generateCustomElementWrapper, createSetupElement } from 'dry-suite';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { map, filter, first, distinctUntilChanged, debounceTime } from 'rxjs/operators';
const legacyIframeResizerMessagePrefix = '[iFrameSizer]';
const tectonOutletBaseObservedAttrs = [
    { name: 'name', reflectAs: String },
    { name: 'minHeight', reflectAs: String },
    { name: 'overpanel', reflectAs: Boolean }
];
export function makeTectonElement(elementName, { messageBus, props, moduleId, tectonOutletAdaptor, resize }) {
    const platformEngine = (customElement, props$) => {
        const DOM$ = new BehaviorSubject(document.createElement('div'));
        const settled$ = new BehaviorSubject(false);
        const outletNameSubscription = props$
            .pipe(map(props => props.name), filter(outletName => !!outletName), map(outletName => {
            // platform outlets do not have module Ids..as they did not originate in modules.
            // this code just makes sure we don't have duplicate periods due to an empty moduleId
            const moduleIdIfExists = moduleId ? `${moduleId}.` : '';
            return `${moduleIdIfExists}${outletName}`;
        }), distinctUntilChanged(), first())
            .subscribe(outletPath => {
            const adaptorResponse = tectonOutletAdaptor(messageBus, outletPath, customElement, props$, resize);
            const domSubscription = adaptorResponse.DOM$.subscribe(DOM$);
            const settledSubscription = adaptorResponse.settled$.subscribe(settled$);
            const teardownSubscription = adaptorResponse.teardown$.subscribe(teardown$);
            const effectSubscription = adaptorResponse.effect$.subscribe(effect => {
                if (customElement.__attached) {
                    effect();
                }
                else {
                    customElement.__onAttached.push(effect);
                }
            });
            adaptorResponse.teardown$.next(() => {
                domSubscription.unsubscribe();
                settledSubscription.unsubscribe();
                teardownSubscription.unsubscribe();
                effectSubscription.unsubscribe();
            });
        });
        const teardown$ = new BehaviorSubject(() => {
            outletNameSubscription.unsubscribe();
        });
        return {
            DOM$,
            settled$,
            teardown$
        };
    };
    return generateCustomElementWrapper(elementName, platformEngine, {
        useShadowDOM: true,
        props: tectonOutletBaseObservedAttrs.concat(props),
        css: `
            .root {
                height: 100%;
                width: 100%;
            }
            .outlet-wrapper.scrollable {
                height: 100%;
            }
            .outlet-wrapper .loading-wrapper {
                height: 44px;
                width: 100%;
                align-items: center;
                justify-content: center;
            }
            .outlet-wrapper .loading-wrapper:not([hidden]) {
                display: flex;
            }
            .outlet-wrapper.scrollable .loading-wrapper,
            .outlet-wrapper.scrollable iframe {
                height: 100%;
            }
            .outlet-wrapper iframe {
                width: 100%;
                height: auto;
                border: none;
            }
            .outlet-wrapper iframe:not([hidden]) {
                display: inline-block;
            }
        `
    });
}
export const setupTectonElement = createSetupElement(makeTectonElement);
export function setupOutletIFrameWrapper(src, moduleId, authPayload, iframeTitle, maxHeight, maxWidth, canScroll, isFormPost, additionalDomainsConfig, allowDirectivesConfig) {
    const outletDOM = document.createElement('div');
    outletDOM.className = 'outlet-wrapper';
    const loaderSlot = document.createElement('slot');
    loaderSlot.name = 'loading-wrapper';
    const onLoads = [];
    const iframe = document.createElement('iframe');
    if (isFormPost) {
        const tectonSubmitForm = document.createElement('form');
        authPayload.forEach(function (item) {
            const tokenInput = document.createElement('input');
            tokenInput.name = item.key;
            tokenInput.setAttribute('value', item.value);
            tectonSubmitForm.appendChild(tokenInput);
        });
        tectonSubmitForm.hidden = true;
        tectonSubmitForm.method = 'post';
        tectonSubmitForm.action = src;
        tectonSubmitForm.target = moduleId;
        tectonSubmitForm.enctype = 'multipart/form-data';
        iframe.name = moduleId;
        outletDOM.appendChild(tectonSubmitForm);
        onLoads.push(function () {
            tectonSubmitForm.submit();
            outletDOM.removeChild(tectonSubmitForm);
        });
    }
    else {
        iframe.src = src;
    }
    iframe.title = iframeTitle;
    iframe.dataset.moduleId = moduleId;
    iframe.style.maxHeight = maxHeight;
    iframe.style.maxWidth = maxWidth;
    iframe.allow = _computeAllowString(additionalDomainsConfig, allowDirectivesConfig, src);
    // sdk uses this to send CONNECT to the right place
    outletDOM.appendChild(iframe);
    const initLegacyIframeResizer = () => {
        iframe.contentWindow &&
            iframe.contentWindow.postMessage(`${legacyIframeResizerMessagePrefix}${moduleId}:0:false:false:32:true:true:null:documentElementOffset`, '*');
    };
    onLoads.push(initLegacyIframeResizer);
    iframe.onload = function () {
        onLoads.forEach(onLoad => {
            onLoad();
        });
        iframe.onload = function () {
            return;
        };
    };
    if (canScroll) {
        outletDOM.classList.add('scrollable');
    }
    else {
        iframe.style.setProperty('overflow-y', 'hidden');
        iframe.scrolling = 'no';
    }
    iframe.hidden = true;
    outletDOM.appendChild(loaderSlot);
    return outletDOM;
}
function _computeAllowString(additionalDomainsConfig = [], allowDirectivesConfig = ['geolocation', 'camera'], src) {
    const url = new URL(src);
    let additionalDomains = ["'self'", `${url.origin}`];
    if (additionalDomainsConfig && additionalDomainsConfig.length > 0) {
        additionalDomains = [...additionalDomains, ...additionalDomainsConfig];
    }
    return allowDirectivesConfig.reduce(function (prev, cur) {
        return prev + `${cur} ${additionalDomains.join(' ')}; `;
    }, '');
}
export function buildLoaders(loadingOptions) {
    const loaders = [];
    if (loadingOptions && loadingOptions.loaders) {
        const rowCount = loadingOptions.loaders.reduce((highestRow, currentOption) => currentOption.row > highestRow ? currentOption.row : highestRow, 0);
        for (let i = 0; i < rowCount; i++) {
            loaders.push([]);
        }
        for (const newLoader of loadingOptions.loaders) {
            const loader = document.createElement('q2-loading');
            loader.setAttribute('shape', newLoader.shape);
            loader.setAttribute('type', newLoader.type);
            loader.className = 'q2-col';
            newLoader.classList && loader.classList.add(...newLoader.classList);
            newLoader.style && loader.setAttribute('style', newLoader.style);
            newLoader.modifiers && loader.setAttribute('modifiers', newLoader.modifiers);
            newLoader.counts && loader.setAttribute('counts', newLoader.counts);
            newLoader.ariaLabel && loader.setAttribute('ariaLabel', newLoader.ariaLabel);
            loaders[newLoader.row - 1].push(loader);
        }
    }
    else {
        const loader = document.createElement('q2-loading');
        loaders.push([]);
        loader.classList.add('mrg(a)');
        loaders[0].push(loader);
    }
    return loaders;
}
export function setupOutletInfo(messageBus, getModuleId, getContent) {
    return messageBus.sendResponse('requestOutletInfo', ({ moduleId }) => {
        if (moduleId !== getModuleId()) {
            return null;
        }
        const { iframeEl } = getContent();
        const bcRect = iframeEl.getBoundingClientRect();
        if (bcRect && Object.values(bcRect.toJSON()).find(v => v !== 0)) {
            return Promise.resolve(bcRect);
        }
        else {
            return null;
        }
    });
}
export function setupOutletIFrameLoading(messageBus, getModuleId, getContent) {
    return messageBus
        .onMessage('setFetching')
        .pipe(filter(response => {
        // filter out message meant for other outletIds
        return response.value.moduleId === getModuleId();
    }))
        .subscribe(response => {
        const { loaderWrapper, iframeEl, minHeight = '0px' } = getContent();
        if (loaderWrapper && iframeEl) {
            const { fetching } = response.value;
            loaderWrapper.hidden = !fetching;
            iframeEl.hidden = fetching;
            loaderWrapper.style.minHeight = fetching ? minHeight : '0px';
        }
    });
}
export function setupOutletSetTitleListener(messageBus, getModuleId, getContent) {
    return messageBus
        .onMessage('setTitle')
        .pipe(filter(response => {
        // filter out message meant for other outletIds
        return response.value.moduleId === getModuleId();
    }))
        .subscribe(response => {
        const { iframeEl } = getContent();
        iframeEl.title = response.value.title;
    });
}
export function setupOutletIFrameResizer(messageBus, getModuleId, getContent, resize) {
    const v1Listener = fromEvent(window, 'message').pipe(filter(e => typeof e.data === 'string' && e.data.includes('iFrameSizer')), map(e => {
        const messageData = e.data
            .substr(legacyIframeResizerMessagePrefix.length)
            .split(':');
        const [moduleId, height] = messageData;
        return {
            value: {
                moduleId,
                height
            }
        };
    }));
    const v2Listener = messageBus.onMessage('resizeIframe');
    return merge(v1Listener, v2Listener)
        .pipe(filter(response => {
        // filter out message meant for other outletIds
        return response.value.moduleId === getModuleId();
    }), distinctUntilChanged((oldResponse, newResponse) => oldResponse.value.height === newResponse.value.height), debounceTime(5))
        .subscribe(response => {
        const { height } = response.value;
        const { iframeEl } = getContent();
        if (iframeEl) {
            iframeEl.style.setProperty('height', `${height}px`);
            if (resize) {
                resize();
            }
        }
    });
}
//# sourceMappingURL=tecton-outlet-base.js.map